import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  FileText, TrendingUp, Send, AlertCircle, Clock, CheckCircle,
  PlusCircle, ArrowRight, Download
} from 'lucide-react';
import { getStatusLabel, getStatusColor, getSubjectLabel } from '../lib/subjectFields';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';

const KPI_ICONS = {
  total: FileText,
  this_month: TrendingUp,
  sent: Send,
  to_qualify: AlertCircle,
  waiting: Clock,
  signed: CheckCircle,
};

const PIE_COLORS = ['#397DFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#6B7280'];

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard/stats').then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${baseUrl}/api/leads/export/excel`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur serveur');
      }
      
      const blob = await response.blob();
      const filename = `export_leads_pde_${new Date().toISOString().slice(0,10)}.xlsx`;
      
      // Use file-saver library for reliable downloads
      saveAs(blob, filename);
      
      toast.success('Export Excel telecharge !');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Erreur - essayez de rafraichir la page');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-primary border-t-transparent animate-spin" style={{borderRadius:'50%'}} /></div>;
  }
  if (!stats) return <p className="text-muted-foreground">Erreur de chargement</p>;

  const kpis = [
    { label: 'Total leads', value: stats.total, icon: 'total', color: '#0B1120', link: '/leads' },
    { label: 'Ce mois-ci', value: stats.this_month, icon: 'this_month', color: '#397DFF', link: '/leads' },
    { label: 'Envoyes', value: stats.status_counts?.envoye_partenaire || 0, icon: 'sent', color: '#3B82F6', link: '/leads?status=envoye_partenaire' },
    { label: 'A qualifier', value: stats.status_counts?.a_qualifier || 0, icon: 'to_qualify', color: '#F59E0B', link: '/leads?status=a_qualifier' },
    { label: 'En attente', value: stats.status_counts?.en_attente_infos || 0, icon: 'waiting', color: '#F97316', link: '/leads?status=en_attente_infos' },
    { label: 'Signes', value: stats.status_counts?.signe || 0, icon: 'signed', color: '#059669', link: '/leads?status=signe' },
  ];

  const subjectData = Object.entries(stats.subject_counts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: getSubjectLabel(k), value: v, subject: k }));

  const statusData = Object.entries(stats.status_counts || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: getStatusLabel(k), value: v, status: k }));

  return (
    <div className="space-y-6 fade-in" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0B1120]">Tableau de bord</h1>
          <p className="text-sm text-muted-foreground mt-1">Vue d'ensemble de l'activite EE</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleExportExcel}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <Download size={16} /> {exporting ? 'Export...' : 'Export Excel'}
          </Button>
          <Button
            className="bg-[#397DFF] hover:bg-[#2563EB] text-white gap-2"
            onClick={() => navigate('/leads/new')}
            data-testid="dashboard-new-lead-btn"
          >
            <PlusCircle size={16} /> Nouveau lead
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-grid">
        {kpis.map(kpi => {
          const Icon = KPI_ICONS[kpi.icon];
          return (
            <Card key={kpi.label} className="border shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(kpi.link)} data-testid={`kpi-${kpi.icon}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon size={18} style={{ color: kpi.color }} strokeWidth={1.5} />
                </div>
                <p className="text-2xl font-heading font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading">Leads par sujet</CardTitle>
          </CardHeader>
          <CardContent>
            {subjectData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={subjectData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#397DFF" name="Leads" cursor="pointer"
                    onClick={(data) => { if (data?.subject) navigate(`/leads?subject=${data.subject}`); }} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-10 text-center">Aucune donnee</p>
            )}
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading">Repartition par statut</CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-10 text-center">Aucune donnee</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent leads & follow-ups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-heading">Leads recents</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/leads')} data-testid="view-all-leads-btn">
              Voir tout <ArrowRight size={14} className="ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {(stats.recent_leads || []).slice(0, 5).map(lead => (
                <button
                  key={lead.id}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`recent-lead-${lead.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{lead.client_name}</p>
                    <p className="text-xs text-muted-foreground">{getSubjectLabel(lead.subject)} - {lead.city}</p>
                  </div>
                  <Badge variant="outline" className="ml-2 text-[10px] shrink-0" style={{ borderColor: getStatusColor(lead.status), color: getStatusColor(lead.status) }}>
                    {getStatusLabel(lead.status)}
                  </Badge>
                </button>
              ))}
              {(!stats.recent_leads || stats.recent_leads.length === 0) && (
                <p className="text-sm text-muted-foreground py-6 text-center">Aucun lead</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading">A suivre / relancer</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {(stats.to_follow_up || []).slice(0, 5).map(lead => (
                <button
                  key={lead.id}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                  data-testid={`followup-lead-${lead.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{lead.client_name}</p>
                    <p className="text-xs text-muted-foreground">{getSubjectLabel(lead.subject)} - {lead.city}</p>
                  </div>
                  <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                    {new Date(lead.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </button>
              ))}
              {(!stats.to_follow_up || stats.to_follow_up.length === 0) && (
                <p className="text-sm text-muted-foreground py-6 text-center">Aucun lead a suivre</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
